import _ from 'lodash';
import { ClearSaleScoreItem } from '~/typings/entities/person';
import moment from 'moment';

export enum ScoreSteps {
  initial = 'initial',
  emailValidated = 'emailValidated',
  emailNotValidated = 'emailNotValidated',
  smsValidated = 'smsValidated',
  smsNotValidated = 'smsNotValidated',
  whatsappValidated = 'whatsappValidated',
  whatsappNotValidated = 'whatsappNotValidated',
  change = 'change',
  tokenSent = 'tokenSent',
}

export const ScoreStepTitle = {
  [ScoreSteps.initial]: 'Score inicial',
  [ScoreSteps.emailValidated]: 'Validação de token',
  [ScoreSteps.smsValidated]: 'Validação de token',
  [ScoreSteps.emailNotValidated]: 'Validação de token',
  [ScoreSteps.smsNotValidated]: 'Validação de token',
  [ScoreSteps.whatsappValidated]: 'Validação de token',
  [ScoreSteps.whatsappNotValidated]: 'Validação de token',
  [ScoreSteps.change]: 'Mudança de score',
  [ScoreSteps.tokenSent]: 'Envio de token',
};

const ScoreStepText = {
  [ScoreSteps.emailValidated]: 'Token validado por E-mail',
  [ScoreSteps.emailNotValidated]: 'Token não validado por E-mail',
  [ScoreSteps.smsValidated]: 'Token validado por SMS',
  [ScoreSteps.smsNotValidated]: 'Token não validado por SMS',
  [ScoreSteps.whatsappValidated]: 'Token validado por Whatsapp',
  [ScoreSteps.whatsappNotValidated]: 'Token não validado por Whatsapp',
};

export type ScoreHistoryItem = {
  date?: string;
  score: number;
  step: ScoreSteps;
  text?: string;
};

function getSendTokenText(score: ClearSaleScoreItem) {
  const sendMethods = [];

  if (score.emailSentDate) {
    sendMethods.push('E-mail');
  }
  if (score.smsSentDate) {
    sendMethods.push('SMS');
  }
  if (score.whatsappSentDate) {
    sendMethods.push('WhatsApp');
  }

  return `Envio de token por ${sendMethods.join(', ').replace(/, ([^,]*)$/, ' e $1')}`;
}

function getText(step: ScoreSteps, score: ClearSaleScoreItem) {
  if (step === ScoreSteps.tokenSent) {
    return getSendTokenText(score);
  }

  return ScoreStepText?.[step];
}

function detectScoreChanges(array: ClearSaleScoreItem[]) {
  const reversedArray = array.slice().reverse();
  return reversedArray.reduce((changes, current, index, arr) => {
    if (index > 0 && current.score !== arr[index - 1].score) {
      changes.push(current);
    }
    return changes;
  }, []);
}

function formatScores(scores: ClearSaleScoreItem[]) {
  const scoreHistory = [];

  // Token sent
  const tokenValidationsSent = scores
    .filter((el) => el.emailSentDate || el.smsSentDate || el.whatsappSentDate)
    .map((el) => ({
      date: el.emailSentDate || el.smsSentDate || el.whatsappSentDate,
      step: ScoreSteps.tokenSent,
      text: getText(ScoreSteps.tokenSent, el),
    }));
  const uniqueTokenValidationsSent = _.uniqBy(tokenValidationsSent, 'date');

  if (uniqueTokenValidationsSent) {
    scoreHistory.push(...uniqueTokenValidationsSent);
  }

  // Email validation
  const emailValidations = scores
    .filter((el) => el.validatedEmail !== null)
    .map((el) => {
      const step = el.validatedEmail ? ScoreSteps.emailValidated : ScoreSteps.emailNotValidated;

      return {
        date: el.validationDate,
        text: getText(step, el),
        step,
      };
    });
  const uniqueEmailValidations = _.uniqBy(emailValidations, 'validationDate');

  if (uniqueEmailValidations) {
    scoreHistory.push(...uniqueEmailValidations);
  }

  // SMS validation
  const smsValidations = scores
    .filter((el) => el.validatedSms !== null)
    .map((el) => {
      const step = el.validatedSms ? ScoreSteps.smsValidated : ScoreSteps.smsNotValidated;

      return {
        date: el.validationDate,
        text: getText(step, el),
        step,
      };
    });
  const uniqueSmsValidations = _.uniqBy(smsValidations, 'date');

  if (uniqueSmsValidations.length) {
    scoreHistory.push(...uniqueSmsValidations);
  }

  // Whatsapp validation
  const whatsappValidations = scores
    .filter((el) => el.validatedWhatsapp !== null)
    .map((el) => {
      const step = el.validatedWhatsapp ? ScoreSteps.whatsappValidated : ScoreSteps.whatsappNotValidated;

      return {
        date: el.validationDate,
        text: getText(step, el),
        step,
      };
    });
  const uniqueWhatsappValidations = _.uniqBy(whatsappValidations, 'date');

  if (uniqueWhatsappValidations.length) {
    scoreHistory.push(...uniqueWhatsappValidations);
  }

  // Score changes
  const scoreChanges = detectScoreChanges(scores).map((el) => ({
    date: el.creationDate,
    score: el.score,
    step: ScoreSteps.change,
  }));

  if (scoreChanges.length) {
    scoreHistory.push(...scoreChanges);
  }

  const orderedHistory = _.orderBy(scoreHistory, (o) => moment(o.date), ['desc']);

  // Initial score
  const initial = scores[scores.length - 1];
  orderedHistory.push({
    score: initial.score,
    date: initial.creationDate,
    step: ScoreSteps.initial,
  });

  return orderedHistory;
}

export default formatScores;
